import React, { useCallback, useEffect, useState } from "react";
import { Content } from "antd/es/layout/layout";
import { Button, Input, Select, message, TableColumnsType } from "antd";
import AppModal from "../modals/AppModal";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./DashboardLayout.css";
import GenericTable from "../GeneralTable/GenericTable";
import HeaderOfList from "../../assets/img/dashboardHeader.png";
import { getUserRole } from "../../hooks/getUserRoles";
import { normalizeRole } from "../../utils/roles";

import AddDataModal from "./modals/AddDataModal";
import {
  DashboardLayoutProps,
  DatasetState,
  AddDataChoice,
} from "../../types/types";
import {
  isValidUrl,
  isSupportedExternalSource,
} from "./utils/dashboardUpload.utils";
import { makeSampleSurvey } from "./utils/sampleSurvey.utils";
import {
  uploadInternalDashboard,
  uploadExternalDashboard,
  getDashboards,
  updateDashboard,
  deleteDashboard,
} from "./services/dashboard.service";

const DashboardLayout: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const userRole = getUserRole();
  const { id: projectId } = useParams();

  const [selectionType] = useState<"checkbox">("checkbox");
  const [loading, setLoading] = useState(false);

  const [dashboards, setDashboards] = useState<DashboardLayoutProps[]>([]);
  const [searchCreator, setSearchCreator] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [dateFilter, setDateFilter] = useState<string | undefined>();

  const [editingDashboard, setEditingDashboard] =
    useState<DashboardLayoutProps | null>(null);
  const [editName, setEditName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const [deletingDashboard, setDeletingDashboard] =
    useState<DashboardLayoutProps | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(`pinned_dashboards_${projectId}`);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  });

  const togglePin = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(
        `pinned_dashboards_${projectId}`,
        JSON.stringify(Array.from(next)),
      );
      return next;
    });
  };

  const [isAddDataOpen, setIsAddDataOpen] = useState(false);
  const [addDataChoice, setAddDataChoice] = useState<AddDataChoice>("external");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [linkValue, setLinkValue] = useState<string>("");

  const handleOpenEdit = (record: DashboardLayoutProps) => {
    setEditingDashboard(record);
    setEditName(record.dashboard_name);
  };

  const handleSaveEdit = async () => {
    if (!editingDashboard) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      message.warning(t("layout.editModal.nameEmptyWarning"));
      return;
    }
    setEditLoading(true);
    try {
      await updateDashboard(editingDashboard.id, {
        name: trimmed,
        header_config: editingDashboard.header_config ?? {},
        footer_config: editingDashboard.footer_config ?? {},
      });
      message.success(t("layout.messages.renameSuccess"));
      setEditingDashboard(null);
      fetchDashboards();
    } catch {
      message.error(t("layout.messages.renameError"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingDashboard) return;
    setDeleteLoading(true);
    try {
      await deleteDashboard(deletingDashboard.id);
      message.success(t("layout.messages.deleteSuccess"));
      setDeletingDashboard(null);
      fetchDashboards();
    } catch {
      message.error(t("layout.messages.deleteError"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetModalState = () => {
    setIsAddDataOpen(false);
    setSelectedFile(null);
    setLinkValue("");
    setAddDataChoice("external");
  };

  const handleChoiceChange = (value: AddDataChoice) => {
    setAddDataChoice(value);
    setSelectedFile(null);
    setLinkValue("");
  };

  const getDateFrom = (filter: string | undefined): string | undefined => {
    if (!filter) return undefined;
    const now = dayjs();
    switch (filter) {
      case "today":
        return now.startOf("day").toISOString();
      case "week":
        return now.subtract(7, "day").startOf("day").toISOString();
      case "month":
        return now.subtract(30, "day").startOf("day").toISOString();
      case "quarter":
        return now.subtract(3, "month").startOf("day").toISOString();
      default:
        return undefined;
    }
  };

  const fetchDashboards = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);

      const response = await getDashboards({
        projectId,
        search: searchTitle || undefined,
        searchCreator: searchCreator || undefined,
        dateFrom: getDateFrom(dateFilter),
      });

      const mapped: DashboardLayoutProps[] = (response || []).map(
        (item: any) => ({
          key: item.id,
          id: item.id,
          dashboard_name: item.name || "",
          document_type: item.document_type || "-",
          date: item.created_at
            ? dayjs(item.created_at).format("MM/DD/YYYY")
            : "",
          header_config: item.header_config ?? {},
          footer_config: item.footer_config ?? {},
        }),
      );

      setDashboards(mapped);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t("layout.messages.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, searchTitle, searchCreator, dateFilter]);

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  const rowSelection = {
    onChange: (
      _selectedRowKeys: React.Key[],
      _selectedRows: DashboardLayoutProps[],
    ) => {},
    getCheckboxProps: (record: DashboardLayoutProps) => ({
      disabled: record.dashboard_name === "Disabled User",
      name: record.dashboard_name,
    }),
  };

  const columns: TableColumnsType<DashboardLayoutProps> = [
    {
      title: t("layout.table.columns.name"),
      dataIndex: "dashboard_name",
      sorter: (a, b) => a.dashboard_name.localeCompare(b.dashboard_name),
      render: (val: string) => val || t("layout.table.untitledDashboard"),
    },
    {
      title: t("layout.table.columns.type"),
      dataIndex: "document_type",
      sorter: (a, b) => a.document_type.localeCompare(b.document_type),
      render: (val: string) => {
        const v = (val || "").toLowerCase();
        const label = v ? v[0].toUpperCase() + v.slice(1) : "—";
        const cls =
          v === "internal"
            ? "dash-pill dash-pill--internal"
            : v === "external"
              ? "dash-pill dash-pill--external"
              : "dash-pill";
        return <span className={cls}>{label}</span>;
      },
    },
    {
      title: t("layout.table.columns.date"),
      dataIndex: "date",
      sorter: (a, b) => {
        const dateA = dayjs(a.date, "MM/DD/YYYY");
        const dateB = dayjs(b.date, "MM/DD/YYYY");
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      render: (text: string) =>
        text ? (
          text
        ) : (
          <span style={{ color: "red" }}>{t("layout.table.invalidDate")}</span>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_: any, record: DashboardLayoutProps) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Button
            type="link"
            icon={pinnedIds.has(record.id) ? <StarFilled /> : <StarOutlined />}
            style={{ color: pinnedIds.has(record.id) ? "#F5A623" : "#A072EB" }}
            onClick={(e) => togglePin(record.id, e)}
          />
          <Button
            type="link"
            icon={<EyeOutlined />}
            style={{ color: "#A072EB" }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/preview/${projectId}`, {
                state: {
                  sourceType: record.document_type,
                  dashboardId: record.id,
                  fileName: record.dashboard_name,
                  openExisting: true,
                },
              });
            }}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            style={{ color: "#A072EB" }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(record);
            }}
          />
          <Button
            type="link"
            icon={<DeleteOutlined />}
            style={{ color: "#A072EB" }}
            onClick={(e) => {
              e.stopPropagation();
              setDeletingDashboard(record);
            }}
          />
        </div>
      ),
    },
  ];

  const handleContinue = async () => {
    if (!projectId) {
      message.error(t("layout.messages.projectIdMissing"));
      return;
    }

    if (addDataChoice === "external") {
      if (!isValidUrl(linkValue)) {
        message.warning(t("layout.messages.invalidLink"));
        return;
      }

      if (!isSupportedExternalSource(linkValue)) {
        message.warning(t("layout.messages.invalidExternalSource"));
        return;
      }

      try {
        setLoading(true);

        const response = await uploadExternalDashboard({
          projectId,
          externalUrl: linkValue,
          name: linkValue,
        });

        message.success(t("layout.messages.externalCreateSuccess"));
        setIsAddDataOpen(false);

        navigate(`/dashboard/preview/${projectId}`, {
          state: {
            sourceType: "external",
            dashboardId: response.dashboard_id,
            fileName: response.name,
          },
        });

        setLinkValue("");
      } catch (error: any) {
        console.error("External upload failed:", error);
        message.error(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            t("layout.messages.externalCreateError"),
        );
      } finally {
        setLoading(false);
      }

      return;
    }

    if (addDataChoice === "internal") {
      if (!selectedFile) {
        message.warning(t("layout.messages.selectFile"));
        return;
      }

      try {
        setLoading(true);

        const response = await uploadInternalDashboard({
          projectId,
          file: selectedFile,
          name: selectedFile.name,
        });

        message.success(t("layout.messages.uploadSuccess"));
        setIsAddDataOpen(false);

        navigate(`/dashboard/preview/${projectId}`, {
          state: {
            sourceType: "internal",
            dashboardId: response.dashboard_id,
            backendResponse: response,
            fileName: selectedFile.name,
          },
        });

        setSelectedFile(null);
      } catch (error: any) {
        console.error("Upload failed:", error);
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            t("layout.messages.uploadError"),
        );
      } finally {
        setLoading(false);
      }

      return;
    }

    if (addDataChoice === "sample") {
      const dataset = makeSampleSurvey();

      setIsAddDataOpen(false);

      navigate(`/dashboard/preview/${projectId}`, {
        state: {
          sourceType: "sample",
          fileName: dataset.fileName,
          dataset,
        },
      });

      message.success(t("layout.messages.sampleLoaded"));
    }
  };

  return (
    <div style={{ marginLeft: 0 }} className="dashboard-ai-layout">
      <Content className="dashboard-ai-content">
        <div className="header-image-for-dashboard">
          <img style={{ width: "100%" }} src={HeaderOfList} alt="header" />
        </div>

        <section className="kap-card">
          <div
            className="kap-card__head"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "1rem",
              marginBottom: "2rem",
              gap: 16,
            }}
          >
            <div className="kap-card__title">
              <h2>{t("layout.title")}</h2>
            </div>

            <div className="kap-actions">
              {normalizeRole(userRole) !== "viewer" && (
                <Button
                  className="adData-btn"
                  onClick={() => setIsAddDataOpen(true)}
                >
                  <PlusOutlined /> {t("layout.actions.addData")}
                </Button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              marginBottom: "1.5rem",
            }}
          >
            <Input
              allowClear
              placeholder={t("layout.filters.searchByUsername")}
              prefix={<SearchOutlined />}
              value={searchCreator}
              onChange={(e) => setSearchCreator(e.target.value)}
            />
            <Input
              allowClear
              placeholder={t("layout.filters.searchByTitle")}
              prefix={<SearchOutlined />}
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
            />
            <Select
              allowClear
              placeholder={t("layout.filters.filterByDate")}
              value={dateFilter}
              onChange={(v) => setDateFilter(v)}
              options={[
                { value: "today", label: t("layout.filters.dateOptions.today") },
                { value: "week", label: t("layout.filters.dateOptions.week") },
                { value: "month", label: t("layout.filters.dateOptions.month") },
                { value: "quarter", label: t("layout.filters.dateOptions.quarter") },
              ]}
            />
          </div>

          <GenericTable
            rowSelection={{ type: selectionType, ...rowSelection }}
            className="kap-table"
            columns={columns}
            dataSource={[...dashboards].sort((a, b) => {
              const aPin = pinnedIds.has(a.id) ? 0 : 1;
              const bPin = pinnedIds.has(b.id) ? 0 : 1;
              return aPin - bPin;
            })}
            loading={loading}
            pagination={{ showSizeChanger: false }}
            emptyText={t("layout.table.emptyText")}
            onRowClick={(record) => console.log("Row clicked:", record)}
          />

          <AppModal
            open={!!editingDashboard}
            onCancel={() => {
              setEditingDashboard(null);
              setEditName("");
            }}
            onConfirm={handleSaveEdit}
            title={t("layout.editModal.title")}
            description={
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    textAlign: "left",
                    marginTop: "1rem",
                  }}
                >
                  <span style={{ color: "red" }}>*</span> {t("layout.editModal.nameLabel")}
                </div>
                <Input
                  placeholder={t("layout.editModal.namePlaceholder")}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onPressEnter={handleSaveEdit}
                  style={{ marginTop: "0.5rem" }}
                />
              </div>
            }
            subDescription={undefined}
            confirmText={t("layout.editModal.confirmText")}
            cancelText={t("layout.editModal.cancelText")}
            confirmType="primary"
            footer={
              <div className="footer-edit-media">
                <Button
                  onClick={() => {
                    setEditingDashboard(null);
                    setEditName("");
                  }}
                  className="cancel-btn-footer"
                >
                  {t("layout.editModal.cancelText")}
                </Button>
                <Button
                  style={{ backgroundColor: "#4A1C96", color: "white" }}
                  onClick={handleSaveEdit}
                  loading={editLoading}
                >
                  {t("layout.editModal.saveButton")}
                </Button>
              </div>
            }
          />

          <AppModal
            open={!!deletingDashboard}
            loading={deleteLoading}
            onCancel={() => setDeletingDashboard(null)}
            onConfirm={handleConfirmDelete}
            title={t("layout.deleteModal.title")}
            description={t("layout.deleteModal.description")}
            subDescription={t("layout.deleteModal.subDescription")}
            confirmText={t("layout.deleteModal.confirmText")}
            cancelText={t("layout.deleteModal.cancelText")}
            confirmType="primary"
            danger
            className="delete-doc-confirmation-modal"
          />

          <AddDataModal
            open={isAddDataOpen}
            addDataChoice={addDataChoice}
            selectedFile={selectedFile}
            linkValue={linkValue}
            onClose={resetModalState}
            onChoiceChange={handleChoiceChange}
            onLinkChange={setLinkValue}
            onFileChange={setSelectedFile}
            onContinue={handleContinue}
            submitting={loading}
          />
        </section>
      </Content>
    </div>
  );
};

export default DashboardLayout;
